import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import LayoutWrapper from '../../../../shared/components/LayoutWrapper';
import SearchableDropdown from '../../../../shared/components/SearchableDropdown';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { API_CONFIG } from '../../../../config/api';
import { convertRegistrationNumber, hasConvertibleEnglishLetters } from '../../../../shared/utils/mongolianConverter';

interface Customer {
  Oid: string;
  CustomerID: string;
  CustomerName: string;
  [key: string]: any;
}

interface ExcelRow {
  rowNumber: number;
  lastName: string;
  firstName: string;
  register: string;
  amount: number;
}

interface ProcessResult {
  rowNumber: number;
  lastName?: string;
  firstName?: string;
  register: string;
  amount: number;
  success: boolean;
  message: string;
  posRno?: string;
  apiName?: string;
}

interface Receipt {
  month?: number;
  posRno?: string;
  date?: string;
  name?: string;
  register?: string;
  amount?: number;
  lottery?: string;
  status?: string | number;
  statusCode?: number;
  prSid?: number | string;
}

interface ExcelCell {
  value: any;
  text: string;
  format: string;
}

const getCell = (cell: any): ExcelCell => {
  if (cell && typeof cell === 'object' && 'text' in cell && 'value' in cell && 'format' in cell) {
    return cell as ExcelCell;
  }
  return { value: cell, text: String(cell ?? ''), format: '' };
};

const cellText = (cell: any) => {
  const c = getCell(cell);
  const text = String(c.text ?? '').trim();
  if (text) return text;
  if (c.value === null || c.value === undefined || c.value === '') return '';
  return String(c.value).trim();
};

const looksLikeAmountFormat = (cell: any) => {
  const c = getCell(cell);
  const text = cellText(c).replace(/^-/, '').replace(/\s/g, '');
  const format = String(c.format || '').toLowerCase();
  if (/0\.00/.test(format) || /#,##0/.test(format) || format.includes('accounting')) return true;
  if (/^\d{1,3}(,\d{3})+\.\d{2}$/.test(text)) return true;
  if (/^\d+\.\d{2}$/.test(text)) return true;
  return false;
};

const isRegisterHeader = (header: any) => {
  const n = normalizeHeader(header);
  if (!n) return false;
  if (['register', 'регистр', 'рд', 'rd', 'pin', 'ttd', 'regno'].includes(n)) return true;
  return n.includes('регистр') || n.includes('регистер');
};

const isAmountHeader = (header: any) => {
  const n = normalizeHeader(header);
  return ['amount', 'дүн', 'дун', 'үнэ', 'уне', 'мөнгө', 'монго', 'amt', 'price'].includes(n);
};

const isLastNameHeader = (header: any) => {
  const n = normalizeHeader(header);
  return ['овог', 'ovog', 'lastname', 'surname'].includes(n);
};

const isFirstNameHeader = (header: any) => {
  const n = normalizeHeader(header);
  return ['нэр', 'ner', 'firstname', 'givenname'].includes(n);
};

const REGISTER_PATTERN = /^[А-ЯЁӨҮA-Z]{2}\d{8}$/;

const looksLikePersonRegister = (value: string) =>
  REGISTER_PATTERN.test(String(value || '').replace(/\s+/g, '').toUpperCase());

const looksLikeRegister = (value: string) =>
  looksLikePersonRegister(value) || /^\d{7,12}$/.test(value);

const looksLikeBankAccount = (value: any) => {
  if (looksLikeAmountFormat(value)) return false;
  const c = getCell(value);
  if (typeof c.value === 'number' && Number.isInteger(c.value) && c.value >= 10000000) return true;
  const raw = cellText(c).replace(/\s/g, '');
  if (!raw || raw.includes('.')) return false;
  return /^\d{8,16}$/.test(raw);
};

const isMoneyAmount = (value: any) => {
  if (looksLikeBankAccount(value)) return false;
  const amount = parseAmount(value);
  return Number.isFinite(amount) && amount > 0;
};

const findAmountOnRow = (row: any[], registerCol: number): number | null => {
  const formatted: number[] = [];
  const fallback: number[] = [];
  row.forEach((cell, c) => {
    if (c === registerCol) return;
    if (cell === null || cell === undefined || cell === '') return;
    if (looksLikePersonRegister(normalizeRegister(cell))) return;
    const amount = parseAmount(cell);
    if (!(amount > 0)) return;
    if (looksLikeAmountFormat(cell)) {
      formatted.push(amount);
      return;
    }
    if (isMoneyAmount(cell)) fallback.push(amount);
  });
  if (formatted.length) return formatted[formatted.length - 1];
  return fallback.length ? fallback[fallback.length - 1] : null;
};

const namesLeftOfRegister = (row: any[], registerCol: number) => {
  const texts: string[] = [];
  for (let c = 0; c < registerCol; c++) {
    const text = cellText(row[c]);
    if (!text) continue;
    if (looksLikePersonRegister(normalizeRegister(row[c]))) continue;
    if (/^\d+([.,]\d+)?$/.test(text.replace(/\s/g, ''))) continue;
    texts.push(text);
  }
  if (texts.length >= 2) {
    return { lastName: texts[texts.length - 2], firstName: texts[texts.length - 1] };
  }
  if (texts.length === 1) {
    const parts = texts[0].split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return { lastName: parts[0], firstName: parts.slice(1).join(' ') };
    return { lastName: '', firstName: texts[0] };
  }
  return { lastName: '', firstName: '' };
};

const findFirstPersonRegisterCell = (rows: any[][], maxRows = 50) => {
  const limit = Math.min(rows.length, maxRows);
  for (let r = 0; r < limit; r++) {
    const row = rows[r] || [];
    for (let c = 0; c < row.length; c++) {
      const register = normalizeRegister(row[c]);
      if (looksLikePersonRegister(register)) return { row: r, col: c, register };
    }
  }
  return null;
};

const findColumns = (headers: any[]) => {
  const register = headers.findIndex(isRegisterHeader);
  const amount = headers.findIndex(isAmountHeader);
  const lastName = headers.findIndex(isLastNameHeader);
  const firstName = headers.findIndex(isFirstNameHeader);
  return { register, amount, lastName, firstName };
};

const parseByHeaders = (rows: any[][]): ExcelRow[] => {
  let headerIndex = -1;
  let cols = { register: -1, amount: -1, lastName: -1, firstName: -1 };
  for (let i = 0; i < Math.min(rows.length, 40); i++) {
    const found = findColumns(rows[i] || []);
    if (found.register >= 0 && found.amount >= 0) {
      headerIndex = i;
      cols = found;
      break;
    }
  }
  if (headerIndex < 0) return [];

  const parsed: ExcelRow[] = [];
  for (let i = headerIndex + 1; i < rows.length; i++) {
    const row = rows[i] || [];
    const register = normalizeRegister(row[cols.register]);
    const lastName = cellText(cols.lastName >= 0 ? row[cols.lastName] : '');
    const firstName = cellText(cols.firstName >= 0 ? row[cols.firstName] : '');
    if (!register || !looksLikeRegister(register)) continue;
    const headerAmountCell = row[cols.amount];
    const amount = looksLikeAmountFormat(headerAmountCell) || (isMoneyAmount(headerAmountCell) && !looksLikeBankAccount(headerAmountCell))
      ? parseAmount(headerAmountCell)
      : (findAmountOnRow(row, cols.register) ?? parseAmount(headerAmountCell));
    if (!(amount > 0)) continue;
    parsed.push({
      rowNumber: i + 1,
      lastName,
      firstName,
      register,
      amount: Number.isFinite(amount) ? amount : 0,
    });
  }
  return parsed;
};

const parseByRegisterPattern = (rows: any[][]): ExcelRow[] => {
  const first = findFirstPersonRegisterCell(rows, 50);
  if (!first) return [];

  const parsed: ExcelRow[] = [];
  for (let i = first.row; i < rows.length; i++) {
    const row = rows[i] || [];
    const register = normalizeRegister(row[first.col]);
    if (!register) continue;
    if (!looksLikePersonRegister(register)) break;

    const amount = findAmountOnRow(row, first.col);
    if (!(amount && amount > 0)) continue;

    const names = namesLeftOfRegister(row, first.col);
    parsed.push({
      rowNumber: i + 1,
      lastName: names.lastName,
      firstName: names.firstName,
      register,
      amount,
    });
  }
  return parsed;
};

const parseSheetRows = (rows: any[][]): ExcelRow[] => {
  if (!rows.length) return [];
  const byHeader = parseByHeaders(rows).filter((row) => row.register && row.amount > 0);
  if (byHeader.length) return byHeader;
  return parseByRegisterPattern(rows);
};

const formatNumber = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('mn-MN');
};

const RECEIPT_STATUS_LABELS: Record<number, string> = {
  1: 'Цуцлагдсан',
  2: 'Баталгаажсан',
  3: 'Зассан баримт',
  4: 'Зөрчилтэй баримт',
  5: 'Буцаасан',
  6: 'Баталгаажаагүй буцаалт',
  10: 'Баталгаажаагүй баримт',
};

const formatReceiptStatus = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string' && Number.isNaN(Number(value))) return value;
  const code = Number(value);
  if (Number.isFinite(code) && RECEIPT_STATUS_LABELS[code]) return RECEIPT_STATUS_LABELS[code];
  return String(value);
};

const formatDateKey = (value?: string): string => {
  if (!value) return 'Огноогүй';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const todayParts = () => {
  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return {
    year,
    month,
    day,
    key: `${year}-${month}-${day}`,
    label: `${year} ${month} ${day}`,
  };
};

const todayKey = () => todayParts().key;

const normalizeHeader = (value: any) =>
  cellText(value)
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[_-]/g, '');

const parseAmount = (value: any): number => {
  const cell = getCell(value);
  if (typeof cell.value === 'number' && Number.isFinite(cell.value)) return cell.value;
  const cleaned = cellText(cell).replace(/[^\d.-]/g, '');
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : NaN;
};

const normalizeRegister = (value: any): string => {
  let register = cellText(value);
  if (!register) return '';
  if (hasConvertibleEnglishLetters(register)) {
    register = convertRegistrationNumber(register);
  }
  return register.toUpperCase();
};

const sheetToCells = (sheet: XLSX.WorkSheet): ExcelCell[][] => {
  const ref = sheet['!ref'];
  if (!ref) return [];
  const range = XLSX.utils.decode_range(ref);
  const rows: ExcelCell[][] = [];
  for (let r = range.s.r; r <= range.e.r; r++) {
    const row: ExcelCell[] = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = sheet[addr];
      if (!cell) {
        row.push({ value: '', text: '', format: '' });
        continue;
      }
      row.push({
        value: cell.v ?? '',
        text: String(cell.w ?? cell.v ?? ''),
        format: String(cell.z ?? ''),
      });
    }
    rows.push(row);
  }
  return rows;
};

const TaxfreeExcelUploadPage: React.FC = () => {
  const { userAuth } = useAuth();
  const abortRef = useRef(false);
  const frozenDate = useMemo(() => todayParts(), []);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerOid, setSelectedCustomerOid] = useState('');

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [excelRows, setExcelRows] = useState<ExcelRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  const [processResults, setProcessResults] = useState<ProcessResult[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>({});
  const [showConfirm, setShowConfirm] = useState(false);

  const getToken = () => {
    const token = localStorage.getItem('novaqToken');
    if (!token) throw new Error('NovaQ token олдсонгүй. Дахин нэвтэрнэ үү.');
    return token;
  };

  const fetchCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    setError(null);
    try {
      const token = getToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/customerslist/accountant/customers`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error(`Серверийн алдаа: ${response.status}`);
      const result = await response.json();
      const list: Customer[] = result?.success && Array.isArray(result.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : [];
      list.sort((a, b) => (a.CustomerName || '').localeCompare(b.CustomerName || '', 'mn-MN'));
      setCustomers(list);
    } catch (err: any) {
      setError(err?.message || 'Харилцагчдын жагсаалт татахад алдаа гарлаа');
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const fetchReceipts = useCallback(async (customerOid: string, y: string, m: string) => {
    if (!customerOid) return;
    setLoadingReceipts(true);
    setError(null);
    try {
      const token = getToken();
      const params = new URLSearchParams({
        customerOid,
        year: y,
      });
      if (m) params.set('month', m);
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/ebarimt-api/get-receipt?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || `Баримт татахад алдаа гарлаа (${response.status})`);
      }
      const list: Receipt[] = Array.isArray(result?.receipts) ? result.receipts : [];
      setReceipts(list);
      const today = todayKey();
      const nextExpanded: Record<string, boolean> = {};
      list.forEach((item) => {
        const key = formatDateKey(item.date);
        if (key === today) nextExpanded[key] = true;
      });
      setExpandedDates((prev) => ({ ...nextExpanded, ...prev }));
    } catch (err: any) {
      setError(err?.message || 'Баримт татахад алдаа гарлаа');
      setReceipts([]);
    } finally {
      setLoadingReceipts(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (selectedCustomerOid) {
      fetchReceipts(selectedCustomerOid, frozenDate.year, frozenDate.month);
    } else {
      setReceipts([]);
    }
  }, [selectedCustomerOid, fetchReceipts, frozenDate]);

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.Oid,
        label: `${c.CustomerID || ''} - ${c.CustomerName || ''}`.trim(),
      })),
    [customers]
  );

  const selectedCustomerLabel = useMemo(() => {
    const customer = customers.find((c) => c.Oid === selectedCustomerOid);
    if (!customer) return '';
    return `${customer.CustomerID || ''} - ${customer.CustomerName || ''}`.trim();
  }, [customers, selectedCustomerOid]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setParseError(null);
    setProcessResults([]);
    setExcelRows([]);
    setSelectedFile(null);
    if (!file) return;

    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
      setParseError('Зөвшөөрөгдсөн формат: .xlsx, .xls, .csv');
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellNF: true });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) throw new Error('Excel файл хоосон байна');
      const sheet = workbook.Sheets[sheetName];
      const rows = sheetToCells(sheet);
      const parsed = parseSheetRows(rows);
      if (!parsed.length) {
        setParseError('Файлд АА12345678 загварын регистр');
        setSelectedFile(file);
        return;
      }
      setSelectedFile(file);
      setExcelRows(parsed);
    } catch (err: any) {
      setParseError(err?.message || 'Excel уншихад алдаа гарлаа');
    }
  };

  const handleCreateClick = () => {
    if (!selectedCustomerOid) {
      setError('Харилцагч сонгоно уу');
      return;
    }
    if (!excelRows.length) {
      setError('Excel файл уншина уу');
      return;
    }

    const invalid = excelRows.filter((row) => !row.register || !(row.amount > 0) || !row.lastName || !row.firstName);
    if (invalid.length) {
      setError(`${invalid.length} мөр буруу байна (овог, нэр, регистр эсвэл дүн хоосон). Засаад дахин оролдоно уу.`);
      return;
    }

    setError(null);
    setShowConfirm(true);
  };

  const handleCreateReceipts = async () => {
    setShowConfirm(false);
    abortRef.current = false;
    setProcessing(true);
    setError(null);
    setProcessResults([]);
    setProgress({ current: 0, total: excelRows.length });

    let token = '';
    try {
      token = getToken();
    } catch (err: any) {
      setError(err?.message || 'Нэвтрэх шаардлагатай');
      setProcessing(false);
      return;
    }
    const results: ProcessResult[] = [];

    for (let i = 0; i < excelRows.length; i++) {
      if (abortRef.current) break;
      const row = excelRows[i];
      setProgress({ current: i + 1, total: excelRows.length });
      try {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 120000);
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/ebarimt-api/set-receipt`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerOid: selectedCustomerOid,
            register: row.register,
            amount: row.amount,
            lastName: row.lastName,
            firstName: row.firstName,
            name: `${row.lastName} ${row.firstName}`.trim(),
          }),
          signal: controller.signal,
        });
        window.clearTimeout(timeoutId);
        const result = await response.json().catch(() => ({}));
        const posRno =
          result?.data?.data?.prPosRno ||
          result?.data?.prPosRno ||
          result?.data?.ddtd ||
          result?.ddtd ||
          '';
        results.push({
          rowNumber: row.rowNumber,
          lastName: row.lastName,
          firstName: row.firstName,
          register: row.register,
          amount: row.amount,
          success: Boolean(result?.success),
          message: result?.message || (response.ok ? 'Баримт гарлаа' : `Алдаа ${response.status}`),
          posRno: posRno || undefined,
          apiName: result?.apiName || result?.buyer?.purchaserName,
        });
      } catch (err: any) {
        results.push({
          rowNumber: row.rowNumber,
          lastName: row.lastName,
          firstName: row.firstName,
          register: row.register,
          amount: row.amount,
          success: false,
          message: err?.name === 'AbortError' ? 'Хугацаа хэтэрсэн' : (err?.message || 'Алдаа гарлаа'),
        });
      }
      setProcessResults([...results]);
    }

    setProcessing(false);
    await fetchReceipts(selectedCustomerOid, frozenDate.year, frozenDate.month);
    setExpandedDates((prev) => ({ ...prev, [todayKey()]: true }));
  };

  const groupedReceipts = useMemo(() => {
    const groups = new Map<string, Receipt[]>();
    receipts.forEach((item) => {
      const key = formatDateKey(item.date);
      const list = groups.get(key) || [];
      list.push(item);
      groups.set(key, list);
    });
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [receipts]);

  const successCount = processResults.filter((r) => r.success).length;
  const failCount = processResults.filter((r) => !r.success).length;
  const excelTotal = excelRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Татваргүй баримт — Excel Upload</h1>
          <p className="text-gray-600 mt-2">
            Excel-ээс регистр, дүн, овог, нэр уншина. eBarimt дээрх нэртэй таарвал л баримт үүсгэнэ.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Харилцагч <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={customerOptions}
                value={selectedCustomerOid}
                onChange={setSelectedCustomerOid}
                placeholder={loadingCustomers ? 'Уншиж байна...' : 'Харилцагч сонгох'}
                disabled={loadingCustomers || processing}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Огноо</label>
              <div className="w-full px-2 py-1 h-[30px] flex items-center border border-gray-300 rounded text-sm bg-gray-100 text-gray-800 font-semibold tracking-wider cursor-default select-none">
                {frozenDate.label}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="taxfree-file">
              Excel файл (Овог, Нэр, Регистер дугаар, Дүн)
            </label>
            <input
              id="taxfree-file"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              disabled={processing}
              className="w-full border rounded px-3 py-2"
            />
            {selectedFile && (
              <p className="mt-2 text-sm text-blue-800">
                {selectedFile.name} — <span className="font-bold">{excelRows.length} мөр, нийт дүн {formatNumber(excelTotal)}</span>
              </p>
            )}
          </div>

          {parseError && (
            <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700">{parseError}</div>
          )}
          {error && (
            <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700">{error}</div>
          )}

          {excelRows.length > 0 && (
            <div className="mb-6 overflow-auto max-h-64 border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Овог</th>
                    <th className="px-3 py-2 text-left">Нэр</th>
                    <th className="px-3 py-2 text-left">Регистр</th>
                    <th className="px-3 py-2 text-right">Дүн</th>
                  </tr>
                </thead>
                <tbody>
                  {excelRows.map((row) => (
                    <tr key={row.rowNumber} className="border-t">
                      <td className="px-3 py-2">{row.rowNumber}</td>
                      <td className="px-3 py-2">{row.lastName || <span className="text-red-500">хоосон</span>}</td>
                      <td className="px-3 py-2">{row.firstName || <span className="text-red-500">хоосон</span>}</td>
                      <td className="px-3 py-2">{row.register || <span className="text-red-500">хоосон</span>}</td>
                      <td className="px-3 py-2 text-right">
                        {row.amount > 0 ? formatNumber(row.amount) : <span className="text-red-500">0</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            {processing && (
              <div className="text-sm text-gray-700">
                Баримт үүсгэж байна: {progress.current}/{progress.total}
              </div>
            )}
            <div className="ml-auto flex gap-3">
              {processing && (
                <button
                  type="button"
                  onClick={() => { abortRef.current = true; }}
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Зогсоох
                </button>
              )}
              <button
                type="button"
                onClick={handleCreateClick}
                disabled={!selectedCustomerOid || !excelRows.length || processing}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {processing ? 'Үүсгэж байна...' : 'Баримт дараалан үүсгэх'}
              </button>
            </div>
          </div>
        </div>

        {processResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Excel-ээс үүсгэсэн үр дүн — амжилттай {successCount}, алдаа {failCount}
            </h2>
            <div className="overflow-auto max-h-72 border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Мөр</th>
                    <th className="px-3 py-2 text-left">Овог нэр</th>
                    <th className="px-3 py-2 text-left">Регистр</th>
                    <th className="px-3 py-2 text-right">Дүн</th>
                    <th className="px-3 py-2 text-left">Төлөв</th>
                    <th className="px-3 py-2 text-left">eBarimt нэр</th>
                    <th className="px-3 py-2 text-left">Баримтын дугаар</th>
                    <th className="px-3 py-2 text-left">Мессеж</th>
                  </tr>
                </thead>
                <tbody>
                  {processResults.map((item) => (
                    <tr key={`${item.rowNumber}-${item.register}`} className="border-t">
                      <td className="px-3 py-2">{item.rowNumber}</td>
                      <td className="px-3 py-2">{[item.lastName, item.firstName].filter(Boolean).join(' ') || '-'}</td>
                      <td className="px-3 py-2">{item.register}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(item.amount)}</td>
                      <td className={`px-3 py-2 font-medium ${item.success ? 'text-green-700' : 'text-red-600'}`}>
                        {item.success ? 'Амжилттай' : 'Алдаа'}
                      </td>
                      <td className="px-3 py-2">{item.apiName || '-'}</td>
                      <td className="px-3 py-2">{item.posRno || '-'}</td>
                      <td className="px-3 py-2">{item.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Үүссэн баримт — өдрөөр</h2>
          <p className="text-sm text-gray-500 mb-4">
            {selectedCustomerOid
              ? `Огноо: ${frozenDate.label}`
              : 'Харилцагч сонгоод баримт татна уу'}
          </p>

          {loadingReceipts ? (
            <div className="py-10 text-center text-gray-500">Баримт уншиж байна...</div>
          ) : groupedReceipts.length === 0 ? (
            <div className="py-10 text-center text-gray-500">Баримт олдсонгүй</div>
          ) : (
            <div className="space-y-3">
              {groupedReceipts.map(([date, items]) => {
                const isToday = date === todayKey();
                const open = expandedDates[date] ?? isToday;
                const total = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
                return (
                  <div key={date} className={`border rounded ${isToday ? 'border-blue-400 bg-blue-50/40' : ''}`}>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                      onClick={() => setExpandedDates((prev) => ({ ...prev, [date]: !open }))}
                    >
                      <span className="font-semibold text-gray-800">
                        {date} {isToday ? '(өнөөдөр)' : ''}
                      </span>
                      <span className="text-sm text-gray-600">
                        {items.length} баримт · нийт {formatNumber(total)}
                      </span>
                    </button>
                    {open && (
                      <div className="overflow-auto border-t">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left">Цаг</th>
                              <th className="px-3 py-2 text-left">Нэр</th>
                              <th className="px-3 py-2 text-left">Регистр</th>
                              <th className="px-3 py-2 text-right">Дүн</th>
                              <th className="px-3 py-2 text-left">Баримтын дугаар</th>
                              <th className="px-3 py-2 text-left">Сугалаа</th>
                              <th className="px-3 py-2 text-left">Төлөв</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr key={item.prSid || item.posRno || idx} className="border-t bg-white">
                                <td className="px-3 py-2">{item.date || '-'}</td>
                                <td className="px-3 py-2">{item.name || '-'}</td>
                                <td className="px-3 py-2">{item.register || '-'}</td>
                                <td className="px-3 py-2 text-right">{formatNumber(item.amount)}</td>
                                <td className="px-3 py-2">{item.posRno || '-'}</td>
                                <td className="px-3 py-2">{item.lottery || '-'}</td>
                                <td className="px-3 py-2">{formatReceiptStatus(item.status)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Баримт үүсгэх</h3>
            <p className="text-gray-700 mb-6">
              <span className="font-bold">{selectedCustomerLabel}</span>
              {' '}
              <span className="font-bold">{excelRows.length} мөр, нийт дүн {formatNumber(excelTotal)}</span>
              {' '}итгэлтэй байна уу?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Үгүй
              </button>
              <button
                type="button"
                onClick={handleCreateReceipts}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Тийм
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutWrapper>
  );
};

export default TaxfreeExcelUploadPage;
